// src/anim/clipData.ts — data/clipdata.json → ClipDataSet (ARCHITECTURE §2 ClipData/ClipDataSet, §4.5 hitbox, §4.6 kök hareket, §5.3 ayna).
// three İMPORT ETMEZ, I/O İÇERMEZ: Node testleri (test/helpers/loadClipData.ts readFileSync) ve tarayıcı (view/assets.ts fetch)
// aynı parseClipData'yı çağırır — sim ile görsel tek kaynaktan çıkar. Yükleme anında tahsis serbest; get/byIndex/jointAt/frameOf
// sıcak yolda tahsissizdir (Map/dizi okuması, out parametresi).
//
// Ad çözümleme (clipTable sonek sözleşmesi): '@t0-t1' / '#upper' / '#lower' / '+' → KANONİK klibin ClipData'sına ALIAS (aynı nesne,
// aynı indeks; dilim zaman eksenini kaydırmaz, frameOf değişmez). '~' → AYRI veri: yükleme anında BİR KEZ üretilir (l↔r eklem takası,
// side → −side, weaponTip handslot.l↔r + pos.side/dir.x işareti, lungeLeft → −lungeLeft). '#upper' yalnız UPPER_ALLOWED klipler ve
// hipsYawRange ≤ T.ANIM.upperMaxHipsYawDeg (25°) için çözülür; aksi hâlde indexOf HATA verir (ANIMATION §4 kapısı: türev üretilmez,
// animMap Punch_A#upper'a düşer) — `upperLayerAllowed(set, clip)` ile önceden sorgulanabilir.
//
// Yerel eksenler (ARCHITECTURE §4.5): out.x = fwd (hips'e göre ileri, model +Z), out.y = up (MUTLAK Y, zemin 0), out.z = side
// (sol +, model +X) → core/vec3 localToWorld(out, pos, yaw, fwd, up, side) ile dünyaya. 'center' → (0,0,0): kapsül merkezi + HitboxDef.offset
// hitbox.ts'de eklenir. 'weapon.tip'/'weapon.mid' = handslot.r pos + dir·L / ·L/2; '.l' türevleri handslot.l (dual sol el, kalkan).
import type { ClipData, ClipDataSet, Joint, Vec3 } from '../core/types.ts';
import { CLIP_FPS } from '../config/constants.ts';
import { ALL_CLIP_NAMES, UPPER_ALLOWED, parseClipName } from '../config/clipTable.ts';
import { T } from '../config/tuning.ts';

// ───────────────────────────── JSON şeması (build-clipdata.mjs çıktısı) ─────────────────────────────
export type HandslotKey = 'handslot.l' | 'handslot.r';
export interface ClipJson {
  name: string; set?: string; duration: number; frames: number;
  impact: number; hipsYawRange: number; lungeAxis: number[];
  hipsY: number[]; lungeFwd: number[]; lungeLeft: number[];
  /** eklem → [fwd, up, side] × frames (düz) */
  joints: Record<string, number[]>;
  /** yalnız silahlı/blok kliplerde; yoksa sıfır eğri (alan hep var, hidden class sabit) */
  weaponTip?: Partial<Record<HandslotKey, { pos: number[]; dir: number[] }>>;
}
export interface ClipDataJson {
  generatedBy?: string; source?: string;
  fps: number; restHipsY?: number; joints?: string[]; clipCount?: number;
  clips: ClipJson[];
}

export interface ParseClipDataOptions {
  /** Yükleme anında çözülecek (ayna türevleri üretilecek, alias'ları kaydedilecek) adlar; varsayılan CLIPS'in tamamı.
   *  Kapıyı geçemeyen '#upper' adları sessizce ATLANIR (indexOf çağrısında hata verir); testler bunu doğrular. */
  names?: readonly string[];
  /** #upper kapısı (derece); varsayılan T.ANIM.upperMaxHipsYawDeg. */
  upperMaxHipsYawDeg?: number;
}

const SWAP_LR: Readonly<Record<string, string>> = { 'hand.l': 'hand.r', 'hand.r': 'hand.l', 'foot.l': 'foot.r', 'foot.r': 'foot.l', 'toes.l': 'toes.r', 'toes.r': 'toes.l' };
const swapLR = (joint: string): string => SWAP_LR[joint] ?? joint;

/** Yükleme anı: number[] → Float32Array (kopya). Uzunluk doğrulaması çağıranda. */
const f32 = (a: readonly number[] | undefined, expect: number, what: string): Float32Array => {
  if (!a) return new Float32Array(expect);
  if (a.length !== expect) throw new Error(`clipdata: ${what} uzunluğu ${a.length} ≠ ${expect}`);
  return Float32Array.from(a);
};

/** Bir JSON klibini ClipData'ya çevirir (kanonik; mirrored false). */
function fromJson(c: ClipJson): ClipData {
  const n = c.frames;
  if (!(n >= 1) || Math.round(c.duration * CLIP_FPS) + 1 !== n) throw new Error(`clipdata: ${c.name} frames ${n} ≠ round(${c.duration}·${CLIP_FPS})+1`);
  const joints: Record<string, Float32Array> = {};
  for (const j of Object.keys(c.joints)) joints[j] = f32(c.joints[j], n * 3, `${c.name}.${j}`);
  const tip = (k: HandslotKey): { pos: Float32Array; dir: Float32Array } => {
    const w = c.weaponTip?.[k];
    return { pos: f32(w?.pos, n * 3, `${c.name}.${k}.pos`), dir: f32(w?.dir, n * 3, `${c.name}.${k}.dir`) };
  };
  const ax = c.lungeAxis[0] ?? 1, az = c.lungeAxis[1] ?? 0;
  return {
    name: c.name, duration: c.duration, frames: n,
    joints,
    weaponTip: { 'handslot.l': tip('handslot.l'), 'handslot.r': tip('handslot.r') },
    lungeFwd: f32(c.lungeFwd, n, `${c.name}.lungeFwd`), lungeLeft: f32(c.lungeLeft, n, `${c.name}.lungeLeft`),
    lungeAxis: [ax, az],
    hipsY: f32(c.hipsY, n, `${c.name}.hipsY`),
    hipsYawRange: c.hipsYawRange,
    impact: c.impact, mirrored: false,
  };
}

/** Ayna türevi (ANIMATION §3 / ARCHITECTURE §5.3 sayısal karşılığı): l↔r takası, side → −side, dir.x → −dir.x, lungeLeft → −lungeLeft.
 *  fwd/up/hipsY/hipsYawRange/impact aynı; lungeAxis eksen (yön değil) olduğundan aynı kalır — scaled izdüşüm oranı işaretten bağımsız. */
export function mirrorClipData(src: ClipData): ClipData {
  const n = src.frames;
  const negSide = (a: Float32Array, comp: number): Float32Array => {
    const o = Float32Array.from(a);
    for (let i = comp; i < o.length; i += 3) o[i] = -(o[i] as number) + 0;   // +0: −0 → 0
    return o;
  };
  const joints: Record<string, Float32Array> = {};
  for (const j of Object.keys(src.joints)) joints[swapLR(j)] = negSide(src.joints[j] as Float32Array, 2);
  const wt = (k: HandslotKey): { pos: Float32Array; dir: Float32Array } => {
    const s = src.weaponTip[k];                                   // hedef k ← kaynak karşı el
    return { pos: negSide(s.pos, 2), dir: negSide(s.dir, 0) };   // pos [fwd,up,side] → side; dir [x,y,z] → x (sol ekseni)
  };
  const left = Float32Array.from(src.lungeLeft);
  for (let i = 0; i < n; i++) left[i] = -(left[i] as number) + 0;
  return {
    name: src.name + '~', duration: src.duration, frames: n,
    joints,
    weaponTip: { 'handslot.l': wt('handslot.r'), 'handslot.r': wt('handslot.l') },
    lungeFwd: Float32Array.from(src.lungeFwd), lungeLeft: left,
    lungeAxis: [src.lungeAxis[0], src.lungeAxis[1]],
    hipsY: Float32Array.from(src.hipsY),
    hipsYawRange: src.hipsYawRange,
    impact: src.impact, mirrored: true,
  };
}

/** '#upper' kapısı: UPPER_ALLOWED'da VE hipsYawRange ≤ eşik (ANIMATION §4). Ölçüm: Slice_Horizontal 72.6°, 2H_Slice 100.5°,
 *  Dualwield_Chop 31.2° kapıyı GEÇEMEZ (UPPER_ALLOWED'da olsalar da) → o hava X'ler Punch_A#upper'a düşer (WP7 animMap / WP3). */
export function upperLayerAllowed(set: ClipDataSet, clip: string, maxDeg: number = T.ANIM.upperMaxHipsYawDeg): boolean {
  const base = parseClipName(clip).base;
  if (!UPPER_ALLOWED.has(base)) return false;
  return set.get(base).hipsYawRange <= maxDeg;
}

class ClipDataSetImpl implements ClipDataSet {
  private readonly list: ClipData[] = [];
  private readonly idx = new Map<string, number>();
  constructor(private readonly upperMaxDeg: number) {}

  add(d: ClipData): number { const i = this.list.length; this.list.push(d); this.idx.set(d.name, i); return i; }

  /** Yükleme anı ad çözümü: kanonik → indeks; '~' → ayna verisi (bir kez üretilir); '@/#/+' → alias. Hata: bilinmeyen kanonik ad,
   *  kapıyı geçemeyen '#upper'. Sonuç idx Map'ine yazılır → sonraki get/indexOf tahsissiz. */
  private resolve(name: string): number {
    const p = parseClipName(name);
    const baseIdx = this.idx.get(p.base);
    if (baseIdx === undefined) throw new Error(`clipdata: bilinmeyen kanonik klip '${p.base}' (${name})`);
    let dataIdx = baseIdx;
    if (p.mirror) {
      const mName = p.base + '~';
      let mi = this.idx.get(mName);
      if (mi === undefined) mi = this.add(mirrorClipData(this.list[baseIdx] as ClipData));
      dataIdx = mi;
    }
    if (p.layer === 'upper') {
      const base = this.list[baseIdx] as ClipData;
      if (!UPPER_ALLOWED.has(p.base)) throw new Error(`clipdata: '${p.base}' UPPER_ALLOWED'da değil (#upper türevi yok): ${name}`);
      if (base.hipsYawRange > this.upperMaxDeg) throw new Error(`clipdata: '${p.base}' hipsYawRange ${base.hipsYawRange}° > ${this.upperMaxDeg}° (#upper türevi yok; Punch_A#upper'a düş): ${name}`);
    }
    this.idx.set(name, dataIdx);
    return dataIdx;
  }

  indexOf(clip: string): number {
    const i = this.idx.get(clip);
    return i === undefined ? this.resolve(clip) : i;
  }
  get(clip: string): ClipData { return this.byIndex(this.indexOf(clip)); }
  byIndex(idx: number): ClipData {
    const d = this.list[idx];
    if (d === undefined) throw new Error(`clipdata: indeks ${idx} yok`);
    return d;
  }
  /** Kare = round(t·60), [0, frames−1] aralığına sıkıştırılır; NaN → 0. Dilim/alias zaman eksenini kaydırmadığından dönüşüm yok. */
  frameOf(clipIdx: number, time: number): number {
    const n = this.byIndex(clipIdx).frames;
    const f = Math.round(time * CLIP_FPS);
    return f >= n ? n - 1 : f >= 0 ? f : 0;
  }
  jointAt(out: Vec3, clipIdx: number, joint: Joint, time: number, weaponLen: number): Vec3 {
    const d = this.byIndex(clipIdx);
    const f = this.frameOf(clipIdx, time) * 3;
    switch (joint) {
      case 'weapon.tip': return tipAt(out, d.weaponTip['handslot.r'], f, weaponLen);
      case 'weapon.mid': return tipAt(out, d.weaponTip['handslot.r'], f, weaponLen * 0.5);
      case 'weapon.tip.l': return tipAt(out, d.weaponTip['handslot.l'], f, weaponLen);
      case 'weapon.mid.l': return tipAt(out, d.weaponTip['handslot.l'], f, weaponLen * 0.5);
      case 'center': out.x = 0; out.y = 0; out.z = 0; return out;
      default: {
        const a = d.joints[joint];
        if (a === undefined) throw new Error(`clipdata: '${d.name}' eklemi yok: ${joint}`);
        out.x = a[f] as number; out.y = a[f + 1] as number; out.z = a[f + 2] as number;
        return out;
      }
    }
  }
  /** Kayıtlı ad sayısı / kanonik+ayna veri sayısı (test/debug). */
  get size(): number { return this.list.length; }
  names(): string[] { return [...this.idx.keys()]; }
}

/** dir [x=sol, y=yukarı, z=ileri] model uzayı → yerel (fwd, up, side): fwd += dir.z, up += dir.y, side += dir.x. */
function tipAt(out: Vec3, w: { pos: Float32Array; dir: Float32Array }, f: number, len: number): Vec3 {
  out.x = (w.pos[f] as number) + (w.dir[f + 2] as number) * len;
  out.y = (w.pos[f + 1] as number) + (w.dir[f + 1] as number) * len;
  out.z = (w.pos[f + 2] as number) + (w.dir[f] as number) * len;
  return out;
}

/** JSON (fetch/readFileSync sonucu) → ClipDataSet. Kanonik klipler + `names` içindeki türetilmiş adlar (varsayılan ALL_CLIP_NAMES)
 *  yükleme anında çözülür; kapıyı geçemeyen '#upper' adları atlanır (indexOf'ta hata). Tarayıcıda ve Node'da aynı. */
export function parseClipData(json: ClipDataJson, opts: ParseClipDataOptions = {}): ClipDataSet & { size: number; names(): string[] } {
  if (json.fps !== CLIP_FPS) throw new Error(`clipdata: fps ${json.fps} ≠ ${CLIP_FPS}`);
  const set = new ClipDataSetImpl(opts.upperMaxHipsYawDeg ?? T.ANIM.upperMaxHipsYawDeg);
  for (const c of json.clips) set.add(fromJson(c));
  for (const name of opts.names ?? ALL_CLIP_NAMES) {
    const p = parseClipName(name);
    if (p.layer === 'upper' && !upperLayerAllowed(set, p.base, opts.upperMaxHipsYawDeg ?? T.ANIM.upperMaxHipsYawDeg)) continue;
    set.indexOf(name);
  }
  return set;
}
